import { Alert, Button } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { EmailThumbnailDiagnostic } from "./emailThumbnail.validation";
import { CopyReadiness } from "./emailThumbnail.ui";

type Props = {
  fragment: string;
  diagnostics: EmailThumbnailDiagnostic[];
  readiness: CopyReadiness;
  projectId: string;
};

type CopyFeedback = {
  fragment: string;
  projectId: string;
  readiness: CopyReadiness;
  requestToken: number;
  recoveryFragment: string | null;
  status: string;
};

const readinessMessage: Record<CopyReadiness, string> = {
  ready: "Generated fragment is valid and ready to copy.",
  unsaved: "Generate your latest changes before copying the fragment.",
  blocked: "Resolve the errors below before copying the fragment.",
};

export const EmailThumbnailOutput = ({
  fragment,
  diagnostics,
  readiness,
  projectId,
}: Props) => {
  const [copyFeedback, setCopyFeedback] = useState<CopyFeedback | null>(null);
  const manualCopyRef = useRef<HTMLTextAreaElement>(null);
  const copyRequestTokenRef = useRef(0);
  const copyContextRef = useRef({ fragment, projectId, readiness });
  copyContextRef.current = { fragment, projectId, readiness };

  useEffect(() => {
    copyRequestTokenRef.current += 1;
    setCopyFeedback(null);
  }, [fragment, projectId, readiness]);

  const visibleCopyFeedback =
    copyFeedback !== null &&
    copyFeedback.fragment === fragment &&
    copyFeedback.projectId === projectId &&
    copyFeedback.readiness === readiness &&
    copyFeedback.requestToken === copyRequestTokenRef.current
      ? copyFeedback
      : null;
  const visibleRecoveryFragment = visibleCopyFeedback?.recoveryFragment ?? null;

  useEffect(() => {
    if (visibleRecoveryFragment === null || readiness !== "ready") return;
    manualCopyRef.current?.focus();
    manualCopyRef.current?.select();
  }, [readiness, visibleRecoveryFragment]);

  const copyFragment = async () => {
    const attemptedFragment = fragment;
    const attemptedProjectId = projectId;
    const requestToken = copyRequestTokenRef.current + 1;
    copyRequestTokenRef.current = requestToken;
    const isCurrentRequest = () => {
      const current = copyContextRef.current;
      return (
        copyRequestTokenRef.current === requestToken &&
        current.fragment === attemptedFragment &&
        current.projectId === attemptedProjectId &&
        current.readiness === "ready"
      );
    };
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(attemptedFragment);
      if (!isCurrentRequest()) return;
      setCopyFeedback({
        fragment: attemptedFragment,
        projectId: attemptedProjectId,
        readiness: "ready",
        requestToken,
        recoveryFragment: null,
        status: "Fragment copied to clipboard.",
      });
    } catch (_error) {
      if (!isCurrentRequest()) return;
      setCopyFeedback({
        fragment: attemptedFragment,
        projectId: attemptedProjectId,
        readiness: "ready",
        requestToken,
        recoveryFragment: attemptedFragment,
        status:
          "Automatic copy failed. The complete fragment is selected below.",
      });
    }
  };

  return (
    <section
      className="email-thumbnail-output"
      aria-labelledby="email-thumbnail-output-title"
    >
      <div className="email-thumbnail-section-heading email-thumbnail-output-heading">
        <div>
          <h2 id="email-thumbnail-output-title">HTML fragment</h2>
          <p>
            Paste this table fragment into a CRM Custom HTML or module block.
          </p>
        </div>
        <Button
          type="primary"
          icon={<CopyOutlined />}
          disabled={readiness !== "ready" || !fragment}
          onClick={copyFragment}
        >
          Copy fragment
        </Button>
      </div>

      <div className="email-thumbnail-copy-readiness" aria-live="polite">
        {readinessMessage[readiness]}
      </div>

      {diagnostics.length > 0 && (
        <div
          className="email-thumbnail-diagnostics"
          aria-label="Configuration diagnostics"
        >
          {diagnostics.map((diagnostic, index) => (
            <Alert
              key={`${diagnostic.code}-${
                diagnostic.field || "general"
              }-${index}`}
              type={diagnostic.severity}
              showIcon
              message={diagnostic.message}
            />
          ))}
        </div>
      )}

      <pre
        className="email-thumbnail-source"
        tabIndex={0}
        aria-label="Generated HTML fragment"
      >
        <code>{fragment || "No valid fragment generated yet."}</code>
      </pre>

      {readiness === "ready" && visibleRecoveryFragment !== null && (
        <div className="email-thumbnail-manual-copy">
          <label htmlFor="email-thumbnail-manual-source">
            Automatic copy was unavailable. The entire fragment is selected;
            choose Copy, or press Select All and then Copy.
          </label>
          <textarea
            id="email-thumbnail-manual-source"
            ref={manualCopyRef}
            readOnly
            value={visibleRecoveryFragment}
            rows={8}
            onFocus={(event) => event.currentTarget.select()}
          />
        </div>
      )}
      <div
        className="email-thumbnail-copy-status"
        aria-live="polite"
        role="status"
      >
        {visibleCopyFeedback?.status || ""}
      </div>
    </section>
  );
};
